import type { IntakeTemplate, Phase, Question } from '../templateTypes';

export type Client2026RowDef = {
  id: string;
  axe: number;
  question: string;
  hasMaturity: boolean;
  example?: string;
  maturityScale?: readonly [string, string, string, string, string, string];
  aliases?: readonly string[];
};

export type Client2026ColumnDef = {
  key: string;
  label: string;
  always: boolean;
  responseKey?: string;
  staticField?: 'axe' | 'question' | 'example' | 'maturity_0' | 'maturity_1' | 'maturity_2' | 'maturity_3' | 'maturity_4' | 'maturity_5';
};

export const CLIENT_2026_KMI_RESPONSE_KEYS = {
  asWas: 'KMI_AS_WAS',
  asIs: 'KMI_AS_IS',
  toBe: 'KMI_TO_BE',
} as const;

export const CLIENT_2026_ALL_COL_DEFS: Client2026ColumnDef[] = [
  { key: 'A', label: 'STO', always: true, responseKey: 'B' },
  { key: 'B', label: 'Pilier', always: true, staticField: 'axe' },
  { key: 'C', label: 'Problèmes/besoins/souhaits/menaces/soucis/améliorations', always: true, staticField: 'question' },
  { key: 'D', label: 'KMI As-Was', always: true, responseKey: CLIENT_2026_KMI_RESPONSE_KEYS.asWas },
  { key: 'E', label: 'KMI As-Is', always: true, responseKey: CLIENT_2026_KMI_RESPONSE_KEYS.asIs },
  { key: 'F', label: 'KMI To-Be', always: true, responseKey: CLIENT_2026_KMI_RESPONSE_KEYS.toBe },
  { key: 'G', label: 'Score 0', always: true, staticField: 'maturity_0' },
  { key: 'H', label: 'Score 1', always: true, staticField: 'maturity_1' },
  { key: 'I', label: 'Score 2', always: true, staticField: 'maturity_2' },
  { key: 'J', label: 'Score 3', always: true, staticField: 'maturity_3' },
  { key: 'K', label: 'Score 4', always: true, staticField: 'maturity_4' },
  { key: 'L', label: 'Score 5', always: true, staticField: 'maturity_5' },
  { key: 'M', label: 'Exemples de réponses', always: true, staticField: 'example' },
  { key: 'N', label: 'Réponses du client', always: true, responseKey: 'F' },
  { key: 'O', label: 'Comment sait-on si le problème est réglé?', always: false, responseKey: 'G' },
  { key: 'P', label: 'Priorité selon client', always: true, responseKey: 'H' },
  { key: 'Q', label: 'Priorité selon Directeur TI de BZ', always: true, responseKey: 'I' },
  { key: 'R', label: 'Objectifs SMART', always: false, responseKey: 'J' },
  { key: 'S', label: 'Solutions compréhensives (Technologique)', always: false, responseKey: 'K' },
  { key: 'T', label: 'Solutions compréhensives (Processus)', always: false, responseKey: 'L' },
  { key: 'U', label: 'Solutions compréhensives (Humain)', always: false, responseKey: 'M' },
  { key: 'V', label: 'Coûts (Prix BZ)', always: false, responseKey: 'N' },
  { key: 'W', label: 'Cout chargé au client', always: false, responseKey: 'O' },
  { key: 'X', label: 'Subventions et/ou crédits disponibles', always: false, responseKey: 'W' },
  { key: 'Y', label: 'Contraintes de temps', always: false, responseKey: 'P' },
  { key: 'Z', label: 'À faire dans la prochaine année', always: false, responseKey: 'Q' },
  { key: 'AA', label: 'Exécution int/ext.', always: false, responseKey: 'R' },
  { key: 'AB', label: 'Effort requis en jours', always: false, responseKey: 'EFFORT_DAYS' },
  { key: 'AC', label: "Durée de l'effort", always: false, responseKey: 'S' },
  { key: 'AD', label: 'Risques', always: false, responseKey: 'T' },
  { key: 'AE', label: 'Approches de mitigation', always: false, responseKey: 'U' },
];

export const CLIENT_2026_ALWAYS_COLUMNS = new Set(
  CLIENT_2026_ALL_COL_DEFS.filter((column) => column.always).map((column) => column.key),
);

export const CLIENT_2026_WORKBOOK_MERGES = [
  { s: { r: 0, c: 18 }, e: { r: 0, c: 20 } },
];

export function buildClient2026WorkbookHeaders(): string[][] {
  const firstRow = CLIENT_2026_ALL_COL_DEFS.map((column) => {
    if (column.key === 'S') return 'Solutions compréhensives';
    if (['T', 'U'].includes(column.key)) return '';
    if (['D', 'E', 'F'].includes(column.key)) return column.label.replace('KMI ', 'KMI \r\n');
    return column.label;
  });
  const secondRow = CLIENT_2026_ALL_COL_DEFS.map((column) => {
    if (column.key === 'S') return 'Technologique';
    if (column.key === 'T') return 'Processus';
    if (column.key === 'U') return 'Humain';
    return '';
  });
  return [firstRow, secondRow];
}

export const CLIENT_2026_AXE_TITLES: Record<number, string> = {
  1: 'Gouvernance, stratégie & organisation',
  2: 'Architecture, infrastructure & cloud',
  3: 'Applications, données & intégrations',
  4: 'Services, opérations & expérience utilisateur',
  5: 'Cybersécurité, risque & conformité',
  6: 'Transformation, innovation & IA',
};

const CLIENT_2026_RAW_ROWS: Array<Omit<Client2026RowDef, 'hasMaturity'>> = [
  { id: "client2026_m_a1_01", axe: 1, question: "Les objectifs ou défis d’affaires sont-ils clairement définis ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Non définis", "2 — Partiellement définis", "3 —formellement définis et documentés (top 5)", "4 — suivi réguilèrement pour décision", "5 — documentation révisé régulièrement"] },
  { id: "client2026_m_a1_02", axe: 1, question: "Les objectifs ou défis d’affaires sont-ils priorisés?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucune priorisation", "2 — Priorisation statique", "3 — Priorisation adaptative(révision au besoin)", "4 — Priorisation formelle (via la gouvernance, en tenant compte des coûts et contraintes)", "5 — priorisé en tenant compte des contraintes d'exécution des travaux TI"] },
  { id: "client2026_m_a1_03", axe: 1, question: "Avez-vous des indicateurs de performance d'affaires?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun KPI", "2 — KPI informels (données brutes pas alignées)", "3 — KPI définis", "4 — KPI suivi régulièrement", "5 — KPI utilisé pour la prise de décision"] },
  { id: "client2026_m_a1_04", axe: 1, question: "Avez-vous des indicateurs de performance TI?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun KPI", "2 — KPI informels (données brutes pas alignées)", "3 — KPI définis", "4 — KPI suivi régulièrement", "5 — KPI utilisé pour la prise de décision"] },
  { id: "client2026_m_a1_05", axe: 1, question: "Dans quelle mesure la stratégie TI est-elle alignée avec les objectifs d’affaires ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — aucune stratégie TI", "2 — stratégie TI très peu alignée (intention existe mais elle n'est pas vraiment liée aux objectifs d'affaires)", "3 — stratégie TI partiellement alignée", "4 — Bien alignée (la majorité des décisions TI soutiennent clairement les décisions d'affaire)", "5 — stratégie TI intégrée aux objectifs d'affaires et guide les décisions importantes"] },
  { id: "client2026_m_a1_06", axe: 1, question: "Avez-vous un comité TI structuré ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun comité", "2 —Comité informel (réactif)", "3 — Comité avec gouvernance (opérationnel)", "4 — Comité structuré Aligné (tactique et Stratégique)", "5 — Comité Avancé (gestion de risques)"] },
  { id: "client2026_m_a1_07", axe: 1, question: "Les rôles et responsabilités TI sont-ils défini/documenté/communiqué (RACI)?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Non définis", "2 — définition Informelle", "3 — Documentation informelle", "4 — Documentés formellement (ex. RACI)", "5 — Validée (Comprise et acceptée par tous)"] },
  { id: "client2026_m_a1_08", axe: 1, question: "Avez-vous un budget TI ? ($?)", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun", "2 — ponctuel", "3 — défini et planifié informellement", "4 — défini selon les besoins d’affaires annuel", "5 — Optimisé et piloté selon un plan stratégique"], aliases: ["Avez-vous un budget TI ?"] },
  { id: "client2026_m_a1_09", axe: 1, question: "Avez-vous des politiques TI formelles ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucune", "2 — définition Informelle", "3 — Documentation informelle", "4 — Documentés et révisés régulièrement", "5 — Validée (Comprise et acceptée par tous)"] },
  { id: "client2026_m_a1_10", axe: 1, question: "Êtes-vous capable de suivre l'avancement de vos projets?", maturityScale: ["0 — On ne s'en occupe pas", "1- Pas de suivi", "2 — Suivi informel", "3 — Suivi formel (manuel)", "4 — Suivi formel avec outil (automatisation et visualisation)", "5 — suivi avec alertes et aide à la décision"] },
  { id: "client2026_m_a1_11", axe: 1, question: "À quelle fréquence sont les rencontres" },
  { id: "client2026_m_a1_12", axe: 1, question: "À quelle fréquence souhaitez-vous avoir un suivi sur les tâches en cours? (fréquence de mise à jour)", aliases: ["À quelle fréquence souhaitez-vous avoir un suivi sur les tâches en cours"] },
  { id: "client2026_m_a1_13", axe: 1, question: "Avez-vous des dépenses TI ($?)autres que celles avec BZ?", aliases: ["Avez-vous des dépenses TI autres que celles avec BZ"] },
  { id: "client2026_m_a1_14", axe: 1, question: "Avez-vous un plan TI annuel (feuille de route) détaillé?", aliases: ["Avez-vous un plan TI annuel (roadmap) détaillé?"] },
  { id: "client2026_m_a1_15", axe: 1, question: "Avez-vous des améliorations nécessaires que vous souhaitez/planifiez/documentez mettre en œuvre au sein de votre entreprise?", example: "gestion de projets, gestion de connaissances, optimisation de processus/procédures d'affaires et/ou TI, amélioration de la posture de sécurité, gestion documents, rendement, retention de personnel, pénurie de main-d'œuvre," },
  { id: "client2026_m_a1_16", axe: 1, question: "Nombre d'usager?" },
  { id: "client2026_m_a1_17", axe: 1, question: "Chiffre d'affaires?" },
  { id: "client2026_m_a1_18", axe: 1, question: "Salaire TI local?" },
  { id: "client2026_m_a2_01", axe: 2, question: "L’inventaire de votre architecture TI est-elle documentée et à jour ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucune documentation", "2 — Documentation partielle ou obsolète", "3 — Documentation complète et à jour (révisé moins de 6 mois)", "4 — Documentation révisée régulièrement", "5 — Documentation utilisée pour les décisions"] },
  { id: "client2026_m_a2_02", axe: 2, question: "Les dépendances techniques sont-elles documentées et à jour?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucune documentation", "2 — Documentation partielle ou obsolète", "3 — Documentation complète et à jour (révisé moins de 6 mois)", "4 — Documentation révisée régulièrement", "5 — Documentation utilisée pour les décisions"], aliases: ["Les dépendances techniques sont-elles documentées ?"] },
  { id: "client2026_m_a2_03", axe: 2, question: "Êtes-vous dépendant de fournisseurs critiques ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Ne sais pas", "2 — Plusieurs dépendances  connues sans évaluation de leur criticité", "3 — dépendances sont documentées et évaluées", "4 —les dépendances sont maitrisées(alternatives existantes)", "5 — optimiséee. Gestion et évaluation régulière"] },
  { id: "client2026_m_a2_04", axe: 2, question: "Quel est le niveau de santé actuel de votre infrastructure ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Infrastructure très risquée (plusieurs actifs critiques à risque)", "2 — Infrastructure risquée (au moins un actif critique à risque)", "3 — Infrastructure à surveiller (quelques actifs non-critiques à risque)", "4 — Globalement à jour", "5 — Optimisée avec cycle de renouvellement"] },
  { id: "client2026_m_a2_05", axe: 2, question: "Comment gérez-vous la performance ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun suivi", "2 — Réactif", "3 — Surveillance partielle", "4 — Suivi structuré", "5 — Monitoring prédictif"] },
  { id: "client2026_m_a2_06", axe: 2, question: "Vos infrastructures/équipements sont-ils facilement disponibles ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Inconnue/ non évalué", "2 — disponibilité limitée", "3 — Généralement disponible", "4 — Disponibilité assuré, relève prévue", "5 — Haute disponibilité, redondance complète"] },
  { id: "client2026_m_a2_07", axe: 2, question: "Vos sauvegardes sont-elles fiables?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Inconnue", "2 — Sauvegarde fonctionnelle non testée", "3 — Sauvegarde vérifiée périodiquement", "4 — Restoration testées régulièrement", "5 — Sauvegarde immuable"] },
  { id: "client2026_m_a2_08", axe: 2, question: "Est-ce que votre infrastructure est adéquate pour vos besoins opérationnels actuels?", example: "besoins storage, besoins computationnels, sauvegarde externe, commutateur désuet, pare-feu, cablage réseau, bande passante", maturityScale: ["0 — On ne s'en occupe pas", "1 — Ne sais pas", "2 — Limitations importantes", "3 — répond au besoins actuels (pas de plaintes)", "4 — Optimisé(analyse complète)", "5 — Optimisation continue(analyse conduite régulièrement)"] },
  { id: "client2026_m_a2_09", axe: 2, question: "Avez-vous un plan de modernisation de votre infrastructure?", example: "projet d'expansion/acquisition, nouveau site/bureau, nouveau marché", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun", "2 — Plan informel", "3 — Plan formel", "4 — Plan budgété et planifié", "5 — Plan Directeur révisé régulièrement"] },
  { id: "client2026_m_a2_10", axe: 2, question: "Avez-vous d'autres enjeux pour ce pilier" },
  { id: "client2026_m_a3_01", axe: 3, question: "Avez-vous un inventaire documenté et à jour de vos applications ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucune documentation", "2 — Documentation partielle ou obsolète", "3 — Documentation complète", "4 — Documentation révisée régulièrement", "5 — Documentation utilisée pour les décisions"] },
  { id: "client2026_m_a3_02", axe: 3, question: "Les dépendances techniques sont-elles documentées ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Inconnues", "2 — Documentation partielle ou obsolète", "3 — Documentation complète", "4 — Documentation révisée régulièrement", "5 — Documentation utilisée pour les décisions"] },
  { id: "client2026_m_a3_03", axe: 3, question: "Êtes-vous dépendant de fournisseurs applicatifs ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Ne sais pas", "2 — Plusieurs dépendances  connues sans évaluation de leur criticité", "3 — dépendances sont documentées et évaluées", "4 —les dépendances sont maitrisées(alternatives existantes)", "5 — optimiséee. Gestion et évaluation régulière"] },
  { id: "client2026_m_a3_04", axe: 3, question: "Quel est l’état de santé des applications ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Application très risquée (plusieurs actifs critiques à risque)", "2 — Application risquée (au moins un actif critique à risque)", "3 — Application à surveiller (quelques actifs non-critiques à risque)", "4 — Globalement à jour", "5 — Optimisée avec cycle de renouvellement"] },
  { id: "client2026_m_a3_05", axe: 3, question: "Comment gérez-vous la performance ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun suivi", "2 — Réactif", "3 — Surveillance partielle", "4 — Suivi structuré", "5 — Monitoring prédictif"] },
  { id: "client2026_m_a3_06", axe: 3, question: "Les données sont-elles facilement accessibles/disponibles ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Inconnue/ non évalué", "2 — disponibilité limitée", "3 — Généralement disponible", "4 — Disponibilité assuré, relève prévue", "5 — Haute disponibilité, redondance complète"] },
  { id: "client2026_m_a3_07", axe: 3, question: "Est-ce que vos applications/données sont adéquates pour vos besoins opérationnels actuels?", example: "Gestion des licences, logiciels spécialisés, gestion des données personnelles,", maturityScale: ["0 — On ne s'en occupe pas", "1 — Ne sais pas", "2 — Limitations importantes", "3 — répond au besoins actuels (pas de plaintes)", "4 — Optimisé(analyse complète)", "5 — Optimisation continue(analyse conduite régulièrement)"] },
  { id: "client2026_m_a3_08", axe: 3, question: "Avez-vous un plan de modernisation de vos applications?", example: "projet d'expansion/acquisition, nouveau site/bureau, nouveau marché", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun", "2 — Plan informel", "3 — Plan formel", "4 — Plan budgété et planifié", "5 — Plan Directeur révisé régulièrement"] },
  { id: "client2026_m_a3_09", axe: 3, question: "Avez-vous d'autres enjeux pour ce pilier" },
  { id: "client2026_m_a4_01", axe: 4, question: "Les utilisateurs accèdent-ils facilement aux services techniques?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun canal ou processus structuré", "2 — Email ou téléphone seulement", "3 — Outil partiel", "4 — Portail structuré", "5 — Expérience optimisée"] },
  { id: "client2026_m_a4_02", axe: 4, question: "Les demandes de solution sont-elles bien gérés et/ou prises en charge ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun canal ou processus structuré", "2 — Réactif", "3 — Processus partiel", "4 — Structuré", "5 — Optimisé"] },
  { id: "client2026_m_a4_03", axe: 4, question: "Les temps de résolution sont-ils gérés ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — pas géré", "2 — Géré informellement", "3 — géré formellement", "4 — géré avec un outil", "5 — géré et suivi adapté"] },
  { id: "client2026_m_a4_04", axe: 4, question: "Mesurez-vous la performance du support ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Non", "2 — informel", "3 — Partiellement", "4 — KPI suivis", "5 — Performance optimisée/ajustée"] },
  { id: "client2026_m_a4_05", axe: 4, question: "Avez-vous un processus de gestion des changements ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun", "2 — Informel", "3 — Partiel", "4 — Structuré", "5 — gouverné/ajusté"] },
  { id: "client2026_m_a4_06", axe: 4, question: "Mesurez-vous la satisfaction utilisateur ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Non", "2 — Occasionnellement", "3 — Partiellement", "4 — Régulièrement", "5 — gouverné/ajusté"] },
  { id: "client2026_m_a4_07", axe: 4, question: "Vos procédures sont-elles documentées ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Non", "2 — Partiellement", "3 — Documentée", "4 — communiquée", "5 — révisé régulièrement"] },
  { id: "client2026_m_a4_08", axe: 4, question: "Formation requise des usagers pour l'exploitation des actifs TI est elle adéquates?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Non", "2 — Informelle", "3 — Formation formelle", "4 — Formation gérée", "5 — Formation continue et révisiée"] },
  { id: "client2026_m_a4_09", axe: 4, question: "Expertises/connaissances/compétences (ex. certifications) requises  pour un soutien des actifs TI aux usagers sont-elles adéquates?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Non", "2 — Partiellement", "3 — Profil technique requis documenté", "4 — Plan carrière technique géré adéquatement", "5 — Gestion de la formation avec outil"] },
  { id: "client2026_m_a4_10", axe: 4, question: "Avez-vous des enjeux de soutien techniques pour vos usagers" },
  { id: "client2026_m_a5_01", axe: 5, question: "Avez-vous une gouvernance pour prendre des décisions sur les enjeux de sécurité et conformité", maturityScale: ["0 — On ne s'en occupe pas", "1 — Non défini", "2 — Informelle", "3 — Documentés formellement (ex. RACI)", "4 — Validée (Comprise et acceptée par tous)", "5 — Révisée périodiquement"] },
  { id: "client2026_m_a5_02", axe: 5, question: "Avez-vous une politique de sécurité ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Non défini", "2 — Informelle", "3 — Documentés formellement (ex. ITIL, NIST)", "4 — Validée (Comprise et acceptée par tous)", "5 — Révisée périodiquement"] },
  { id: "client2026_m_a5_03", axe: 5, question: "Évaluez-vous votre posture de sécurité ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Jamais", "2 — Occasionnelle", "3 — Évaluation périodique", "4 — Évaluation structurée", "5 — Amélioration continue et améliorée"] },
  { id: "client2026_m_a5_04", axe: 5, question: "Les risques TI sont-ils identifiés ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun", "2 — Partiellement identifié", "3 — Identifié et documenté", "4 — Évalué et priorisé", "5 — suivi régulier des actions de mitigation, et Réévaluation périodique"] },
  { id: "client2026_m_a5_05", axe: 5, question: "Avez-vous un registre des risques ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun", "2 — Informel", "3 — Formel", "4 — Révisé", "5 — Gouverné"] },
  { id: "client2026_m_a5_06", axe: 5, question: "Avez-vous un registre des incidents ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun", "2 — Informel", "3 — Formel", "4 — Révisé", "5 — Gouverné"] },
  { id: "client2026_m_a5_07", axe: 5, question: "Avez-vous des enjeux de conformité?", example: "Loi 25, ISO 9001, SOC 2, exigences des assurances, ordre prof.", maturityScale: ["0 — On ne s'en occupe pas", "1 — Ne sais pas", "2 — Enjeux identifiés sans prise en charge", "3 — Enjeux en cours de résolution", "4 — Enjeux majoritairement réglés", "5 — Tous les enjeux sont réglés et révisités/ré-évalués à chaque année"] },
  { id: "client2026_m_a5_08", axe: 5, question: "Les données sensibles sont-elles protégées ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Ne sais pas", "2 — Non ou Partiellement", "3 — Protection de base (minimum)", "4 — Protection adéquate (avancée/renforcée)", "5 — Protection complète et surveillée en continue"] },
  { id: "client2026_m_a5_09", axe: 5, question: "Les employés sont-ils formés à la cybersécurité ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucune formation", "2 — Formation ponctuelle", "3 — Formation annuelle", "4 — Programme structuré", "5 — Culture sécurité intégrée (suivi des résultats des formations)"] },
  { id: "client2026_m_a5_10", axe: 5, question: "Les accès sont-ils sécurisés (MFA) ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun contrôle", "2 — Mots de passe seulement", "3 — MFA partiel (sans accès conditionnel)", "4 — MFA généralisé (accès conditionnel activé)", "5 — Gestion avancée (ex. PIM/PAM, moindre privilèges, IAM, JML,…)selon les moindres privilèges"] },
  { id: "client2026_m_a5_11", axe: 5, question: "Gérez-vous les vulnérabilités ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun suivi", "2 — Réactif", "3 — Scan , analyse et correction ponctuel", "4 — Scan , analyse et correction régulier", "5 — Gestion proactive (surveillance et scan automatique…ex. SOC et/ou SIEM)"] },
  { id: "client2026_m_a5_12", axe: 5, question: "Avez-vous une protection endpoint ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucune", "2 — Antivirus simple", "3 — EDR en place", "4 — EDR centralisée et surveillée", "5 — EDR avec SOC"] },
  { id: "client2026_m_a5_13", axe: 5, question: "Surveillez-vous les activités suspectes ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucune", "2 — Logs non analysés", "3 — Surveillance partielle (ex. réception de courriels)", "4 — Surveillance active (ex. personne dédié à la vérification des alertes))", "5 — Surveillance automatique continue (24/7)"] },
  { id: "client2026_m_a5_14", axe: 5, question: "Avez-vous un plan de réponse aux incidents (limiter/cesser les effets néfastes)?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun", "2 — Informel", "3 — Documenté", "4 — Évalué selon les différents tests périodiquement avec les rôles et responsabilités", "5 — Révisé et adapté périodiquement"] },
  { id: "client2026_m_a5_15", axe: 5, question: "Avez-vous un plan de réprise aux incidents (réparer les dommages causés)?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucun", "2 — Informel", "3 — Documenté", "4 — Évalué selon les différents tests périodiquement avec les rôles et responsabilités", "5 — Révisé et adapté périodiquement"] },
  { id: "client2026_m_a5_16", axe: 5, question: "Avez-vous d'autres enjeux de cybersécurité?" },
  { id: "client2026_m_a6_01", axe: 6, question: "Avez-vous une vision de transformation numérique ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Aucune", "2 — Idées non structurées", "3 — Vision documentée", "4 — Vision révisée régulièrement à l'interne", "5 — Vision révisée régulièrement à l'externe"] },
  { id: "client2026_m_a6_02", axe: 6, question: "La transformation est-elle alignée aux objectifs affaires ?", maturityScale: ["0 — On ne s'en occupe pas", "1 — Ne sais pas", "2 — Non", "3 — Partiellement", "4 — Alignée", "5 — Alignement intégré avec suivi KPI"] },
  { id: "client2026_m_a6_03", axe: 6, question: "Avez-vous des enjeux de transformation numérique en lien avec l'automatisation de certaines tâches/processus simples et répétitives?", example: "élimination des documents papiers,télé-travail, Application de l'IA pour l'automatisation de tâche sans le concours de l'humain, Utilisation de l'IA générative pour la gestion de connaissance, exploitation de l'IA pour palier au manque de main d'œuvre", maturityScale: ["0 — On ne s'en occupe pas", "1 — ne sais pas", "2 — Enjeux identifiés sans prise en charge", "3 — Enjeux en cours de résolution", "4 — Enjeux majoritairement réglés", "5 — Tous les enjeux sont réglés et révisités/ré-évalués à chaque année"] },
  { id: "client2026_m_a6_04", axe: 6, question: "Avez-vous des enjeux de transformation numérique en soutien aux employés/décideurs pour augmenter la performance et l'efficacité de ceux-ci?", example: "exploitation de la technologie Agentik (exécution semi-autonome de tâches et décisions) à la gouvernance et l'exploitation d'une entreprise, Gestion de carrière via un agent IA", maturityScale: ["0 — On ne s'en occupe pas", "1 — ne sais pas", "2 — Enjeux identifiés sans prise en charge", "3 — Enjeux en cours de résolution", "4 — Enjeux majoritairement réglés", "5 — Tous les enjeux sont réglés et révisités/ré-évalués à chaque année"] },
  { id: "client2026_m_a6_05", axe: 6, question: "Utilisez-vous l’IA? Si oui, pour quel problème." },
];

export const CLIENT_2026_ROWS: Client2026RowDef[] = CLIENT_2026_RAW_ROWS.map((row) => ({
  ...row,
  hasMaturity: Boolean(row.maturityScale),
}));

/** Do not persist accidental KMI values for yellow questions in the current form. */
export function stripClient2026UnscoredKmis<T>(responses: Record<string, T>): Record<string, T> {
  const yellowIds = new Set(CLIENT_2026_ROWS.filter((row) => !row.hasMaturity).map((row) => row.id));
  return Object.fromEntries(Object.entries(responses).filter(([key]) => {
    const match = /^(client2026_m_a\d+_\d+)__(KMI_AS_WAS|KMI_AS_IS|KMI_TO_BE)$/.exec(key);
    return !match || !yellowIds.has(match[1]);
  }));
}

export function getClient2026StaticColumnValue(def: Client2026RowDef, staticField?: Client2026ColumnDef['staticField']): string | undefined {
  if (!staticField) return undefined;
  if (staticField === 'axe') return String(def.axe);
  if (staticField === 'question') return def.question;
  if (staticField === 'example') return def.example || '';
  const maturityIndex = Number(staticField.replace('maturity_', ''));
  return Number.isInteger(maturityIndex) ? def.maturityScale?.[maturityIndex] || '' : undefined;
}

export function getClient2026ColumnValue(
  def: Client2026RowDef,
  column: Client2026ColumnDef,
  responses: Record<string, unknown>,
): string {
  const staticValue = getClient2026StaticColumnValue(def, column.staticField);
  if (staticValue !== undefined) return staticValue;
  if (!column.responseKey) return '';
  const maturityKeys: readonly string[] = Object.values(CLIENT_2026_KMI_RESPONSE_KEYS);
  if (!def.hasMaturity && maturityKeys.includes(column.responseKey)) return '';
  const value = responses[`${def.id}__${column.responseKey}`];
  return Array.isArray(value) ? value.map(String).join(', ') : String(value ?? '').trim();
}

export function buildClient2026DocumentRows(
  responses: Record<string, unknown>,
): Array<Record<string, string>> {
  return CLIENT_2026_ROWS.map((row) => Object.fromEntries(
    CLIENT_2026_ALL_COL_DEFS.map((column) => [
      column.key,
      getClient2026ColumnValue(row, column, responses),
    ]),
  ));
}

export type Client2026MaturityAxis = {
  axe: number;
  title: string;
  asWas: number | null;
  asIs: number | null;
  toBe: number | null;
  asWasCount: number;
  asIsCount: number;
  toBeCount: number;
};

const parseClient2026MaturityScore = (value: unknown): number | null => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const score = Number(raw);
  return Number.isFinite(score) && score >= 0 && score <= 5 ? score : null;
};

export type Client2026AsWasBaseline = Record<string, number>;

export function mergeClient2026EmptyTestResponses(
  current: Record<string, string | string[]>,
  testResponses: Record<string, string | string[]>,
): Record<string, string | string[]> {
  const merged = { ...current };
  Object.entries(testResponses).forEach(([key, testResponse]) => {
    const value = merged[key];
    const isEmpty = value === undefined
      || (typeof value === 'string' && value.trim() === '')
      || (Array.isArray(value) && value.length === 0);
    if (isEmpty) merged[key] = Array.isArray(testResponse) ? [...testResponse] : testResponse;
  });
  return merged;
}

export function applyClient2026AsWasBaseline(
  responses: Record<string, unknown>,
  baseline: Client2026AsWasBaseline,
): Record<string, unknown> {
  const snapshot = { ...responses };
  CLIENT_2026_ROWS.filter((row) => row.hasMaturity).forEach((row) => {
    delete snapshot[`${row.id}__${CLIENT_2026_KMI_RESPONSE_KEYS.asWas}`];
    const score = parseClient2026MaturityScore(baseline[row.id]);
    if (score !== null) {
      snapshot[`${row.id}__${CLIENT_2026_KMI_RESPONSE_KEYS.asWas}`] = score;
    }
  });
  return snapshot;
}

const averageClient2026Scores = (scores: number[]): number | null => {
  if (scores.length === 0) return null;
  return Math.round((scores.reduce((sum, score) => sum + score, 0) / scores.length) * 100) / 100;
};

export function buildClient2026MaturityProfile(
  responses: Record<string, unknown>,
): Client2026MaturityAxis[] {
  const snapshot = responses;
  return Object.keys(CLIENT_2026_AXE_TITLES).map(Number).sort((a, b) => a - b).map((axe) => {
    const rows = CLIENT_2026_ROWS.filter((row) => row.axe === axe && row.hasMaturity);
    const scoresFor = (responseKey: string) => rows.flatMap((row) => {
      const score = parseClient2026MaturityScore(snapshot[`${row.id}__${responseKey}`]);
      return score === null ? [] : [score];
    });
    const asWas = scoresFor(CLIENT_2026_KMI_RESPONSE_KEYS.asWas);
    const asIs = scoresFor(CLIENT_2026_KMI_RESPONSE_KEYS.asIs);
    const toBe = scoresFor(CLIENT_2026_KMI_RESPONSE_KEYS.toBe);
    return {
      axe,
      title: CLIENT_2026_AXE_TITLES[axe],
      asWas: averageClient2026Scores(asWas),
      asIs: averageClient2026Scores(asIs),
      toBe: averageClient2026Scores(toBe),
      asWasCount: asWas.length,
      asIsCount: asIs.length,
      toBeCount: toBe.length,
    };
  });
}

export function buildClient2026MaturityContext(profile: Client2026MaturityAxis[]): string {
  const format = (value: number | null) => value === null ? 'Non précisé' : `${value.toFixed(2)} / 5`;
  return [
    'MATURITÉ PAR AXE (moyennes calculées uniquement à partir des scores fournis):',
    'Axe | Passé (As-Was) | Présent (As-Is) | Futur (To-Be)',
    ...profile.map((row) => `${row.axe} — ${row.title} | ${format(row.asWas)} | ${format(row.asIs)} | ${format(row.toBe)}`),
    'Règle de référence: au premier portrait, chaque score Passé est figé au score Présent correspondant; les générations suivantes réutilisent cette référence historique immuable.',
  ].join('\n');
}

export function getClient2026MarkdownColumnValue(
  def: Client2026RowDef,
  column: Client2026ColumnDef,
  readValue: (columnLabel: string) => string,
): string {
  const staticValue = getClient2026StaticColumnValue(def, column.staticField);
  if (staticValue !== undefined) return staticValue;
  const maturityKeys: readonly string[] = Object.values(CLIENT_2026_KMI_RESPONSE_KEYS);
  if (!def.hasMaturity && column.responseKey && maturityKeys.includes(column.responseKey)) return '';
  return readValue(column.label);
}

export function mergeClient2026PhaseResponses(
  besoin: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const merged: Record<string, unknown> = {};
  if (!besoin) return merged;
  for (let phase = 1; phase <= 10; phase += 1) {
    const phaseData = besoin[`phase${phase}_data`];
    if (phaseData && typeof phaseData === 'object' && !Array.isArray(phaseData)) {
      Object.assign(merged, phaseData);
    }
  }
  return merged;
}

export function hasClient2026QuestionResponse(
  questionId: string,
  responses: Record<string, unknown>,
): boolean {
  return Object.entries(responses).some(([key, value]) => {
    if (!key.startsWith(`${questionId}__`) || key === `${questionId}__enabledCols`) return false;
    if (!CLIENT_2026_ROWS.find((row) => row.id === questionId)?.hasMaturity
      && (Object.values(CLIENT_2026_KMI_RESPONSE_KEYS) as string[]).includes(key.slice(questionId.length + 2))) return false;
    if (Array.isArray(value)) return value.some((item) => String(item ?? '').trim() !== '');
    return String(value ?? '').trim() !== '';
  });
}

export function resolveClient2026Schema({
  schema,
  templateVersion,
  isExisting,
}: {
  schema?: unknown;
  templateVersion?: string;
  isExisting: boolean;
}): 1 | 2 | 3 {
  if (Number(schema) === 3) return 3;
  if (Number(schema) === 2) return 2;
  if (Number(schema) === 1) return 1;
  if (isExisting) return 1;
  if (templateVersion === '3.1.0') return 3;
  if (templateVersion === '3.0.0') return 2;
  return 1;
}

/** Accept only known, consistent version/schema pairs for document sources. */
export function isClient2026VersionedSource({ schema, templateVersion }: {
  schema?: unknown;
  templateVersion?: string;
}): boolean {
  return (schema === 2 && templateVersion === '3.0.0')
    || (schema === 3 && templateVersion === '3.1.0');
}

export function isCurrentClient2026Source({
  schema,
  templateVersion,
}: {
  schema?: unknown;
  templateVersion?: string;
}): boolean {
  return schema === 3 && templateVersion === '3.1.0';
}

export function escapeClient2026MarkdownCell(value: unknown): string {
  return String(value ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/\r?\n/g, '<br>')
    .replace(/\|/g, '\\|')
    .trim();
}

export function parseClient2026MarkdownRow(line: string): string[] {
  const source = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  const cells: string[] = [];
  let cell = '';
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];
    if (char === '\\' && index + 1 < source.length && ['\\', '|'].includes(source[index + 1])) {
      cell += source[index + 1];
      index += 1;
    } else if (char === '|') {
      cells.push(cell.trim().replace(/<br\s*\/?>/gi, '\n'));
      cell = '';
    } else {
      cell += char;
    }
  }
  cells.push(cell.trim().replace(/<br\s*\/?>/gi, '\n'));
  return cells;
}

export function indexClient2026MarkdownRows(
  table: { headers: string[]; rows: string[][] },
  normalize: (value: string) => string,
): Map<string, string[]> {
  const headers = table.headers.map(normalize);
  const questionIndex = headers.findIndex((header) =>
    header.includes('problemes/besoins/souhaits') || header === normalize('Question'),
  );
  const categoryIndex = headers.findIndex((header) =>
    header === normalize('Pilier') || header === normalize('Axe') || header === normalize('Pilier / section historique'),
  );
  const indexed = new Map<string, string[]>();
  if (questionIndex < 0) return indexed;
  table.rows.forEach((row) => {
    const question = normalize(row[questionIndex] || '');
    if (!question) return;
    const category = categoryIndex >= 0 ? normalize(row[categoryIndex] || '') : '';
    if (category) indexed.set(`${category}|${question}`, row);
    if (!indexed.has(question)) indexed.set(question, row);
  });
  return indexed;
}

export function findClient2026MarkdownRow(
  def: Client2026RowDef,
  rowByQuestion: Map<string, string[]>,
  normalize: (value: string) => string = (value) => value,
): string[] | undefined {
  for (const candidate of [def.question, ...(def.aliases || [])]) {
    const normalizedQuestion = normalize(candidate);
    const row = rowByQuestion.get(`${def.axe}|${normalizedQuestion}`) || rowByQuestion.get(normalizedQuestion);
    if (row) return row;
  }
  return undefined;
}

const makePhase = (axe: number): Phase => {
  const questions: Question[] = CLIENT_2026_ROWS.filter((row) => row.axe === axe).map((row) => ({
    id: row.id,
    label: row.question,
    ...(row.example ? { helpText: row.example } : {}),
    type: 'text',
    required: false,
    ...(row.hasMaturity ? { maturityScale: row.maturityScale } : {}),
  }));
  return { id: `phase${axe}`, title: `Pilier ${axe} — ${CLIENT_2026_AXE_TITLES[axe] || ''}`, questions };
};

export const templatePriseDeBesoinClient2026: IntakeTemplate = {
  id: 'prise-de-besoin-client-2026',
  name: 'Prise de besoin - Maturité et besoins client',
  description: 'Questionnaire combiné de maturité TI (As-Was, As-Is et To-Be) et de prise de besoins client, structuré selon les six piliers du cadre BZ.',
  version: '3.1.0',
  documentTypeCode: 'PRISE_BESOIN_CLIENT_2026',
  phases: [1, 2, 3, 4, 5, 6].map(makePhase),
};
